import { AbsoluteFill, interpolate, useCurrentFrame } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  MetricCounter,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import {
  HOOK,
  GAP,
  NOTYET,
  FIXES,
  PROMPT,
  RECENCY,
  CITATION,
  PAYOFF,
  OUTRO,
} from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.98,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

// Small on-screen honesty tag, used for the NOT-YET-SHIPPED beat.
const Tag: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 18,
      letterSpacing: "0.16em",
      textTransform: "uppercase",
      color,
      border: `1px solid ${color}`,
      borderRadius: 6,
      padding: "6px 14px",
    }}
  >
    {text}
  </div>
);

/* 01 — HOOK: same PDF, opposite answers */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const reveal = interpolate(frame, [40, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="rag vs notebooklm · same pdf" color={COLORS.cyan} />
          <Big>
            NotebookLM answered it.
            <br />
            My RAG said{" "}
            <span style={{ color: COLORS.red, opacity: 0.35 + reveal * 0.65 }}>
              “NOT IN THE SOURCES”
            </span>
          </Big>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 02 — GAP: whole-document vs top-K */
export const Scene02Gap: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.gap.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="why it won · it skips rag" color={COLORS.violet} />
        <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
          <Panel accent="green" title="notebooklm · whole document" width={520}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
              <div>373-page PDF <span style={{ color: COLORS.dim }}>→ 1M-token window</span></div>
              <div>sees <b style={{ color: COLORS.green }}>every page</b> at once</div>
              <div style={{ color: COLORS.dim }}>no chunking · no retrieval</div>
            </div>
          </Panel>
          <Panel accent="red" title="eakc · top-k chunks" width={520}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
              <div>200 chunks <span style={{ color: COLORS.dim }}>→ retrieve top 15</span></div>
              <div>model saw <b style={{ color: COLORS.red }}>~7%</b> of the doc</div>
              <div style={{ color: COLORS.dim }}>got the date table, not the prose</div>
            </div>
          </Panel>
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 28, color: COLORS.dim, marginTop: 28 }}>
          “not a better pipeline — it bypasses RAG for small-enough docs”
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={GAP} accent="violet" />
  </AbsoluteFill>
);

/* 03 — NOT-YET: the tempting fix I did not ship (honesty beat) */
export const Scene03NotYet: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.notyet.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="the honest part" color={COLORS.amber} />
        <Big size={66}>
          Long-context bypass + HyDE?
          <br />
          <span style={{ color: COLORS.amber }}>Scoped. Not shipped.</span>
        </Big>
        <div style={{ display: "flex", gap: 16, marginTop: 32 }}>
          <Tag text="long-context bypass · roadmap" color={COLORS.amber} />
          <Tag text="hyde · roadmap" color={COLORS.amber} />
        </div>
        <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 28 }}>
          I only put on screen what’s in the repo.
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={NOTYET} accent="amber" />
  </AbsoluteFill>
);

/* 04 — FIXES: the three that actually closed it */
const FIX_PIPE: PipeNode[] = [
  { label: "fix 1", value: "DECISION TREE", sub: "synthesis prompt", accent: "cyan" },
  { label: "fix 2", value: "RECENCY", sub: "list_recent_docs", accent: "green" },
  { label: "fix 3", value: "CITATION PANEL", sub: "click [Source N]", accent: "lime" },
];
export const Scene04Fixes: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.fixes.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 90px" }}>
      <Kicker text="what i shipped instead" color={COLORS.cyan} />
      <div style={{ width: 1640 }}>
        <PipelineGraph nodes={FIX_PIPE} startFrame={6} stepFrames={24} nodeWidth={260} />
      </div>
      <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 40 }}>
        + BM25 OR-tsquery (mig 033) · camelCase split (mig 034)
      </div>
    </AbsoluteFill>
    <Caption lines={FIXES} accent="cyan" />
  </AbsoluteFill>
);

/* 05 — PROMPT: the decision-tree synthesis prompt */
const PROMPT_CODE = `// src/app/api/v1/chat/completions/route.ts
// "Follow this decision tree for EVERY query:"

CASE A  // sources cover the same topic / feature area
  -> You MUST answer from them, even if worded differently.
  -> Cite every claim inline with [Source N].

CASE B  // sources PARTIALLY cover the topic
  -> Answer the covered part. Note the gap at the END.
  -> "A partial answer is ALWAYS more valuable than 'not available'."

CASE C  // a COMPLETELY different domain
  -> Only here: "not available in the knowledge base."
  -> "This should be EXTREMELY rare — less than 1% of queries."`;
export const Scene05Prompt: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.prompt.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 40 }}>
      <CodePanel
        code={PROMPT_CODE}
        language="tsx"
        filename="chat/completions/route.ts · strict synthesis prompt"
        highlightLines={[12, 13]}
        revealFrames={340}
        fontSize={23}
        width={1320}
      />
    </AbsoluteFill>
    <Caption lines={PROMPT} accent="cyan" />
  </AbsoluteFill>
);

/* 06 — RECENCY: list_recent_docs + the eval probe */
export const Scene06Recency: React.FC = () => {
  const frame = useCurrentFrame();
  const fade = interpolate(frame, [70, 100], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.recency.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="embeddings don’t know the date" color={COLORS.green} />
          <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
            <Panel accent="red" title="semantic search · the bug" width={520}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 21, lineHeight: 1.9, color: COLORS.text }}>
                <div>query: “what shipped this week?”</div>
                <div style={{ color: COLORS.red }}>→ ranked a STALE helper #1</div>
                <div style={{ color: COLORS.dim }}>fluent + cited, so graders passed it</div>
              </div>
            </Panel>
            <Panel accent="green" title="list_recent_docs · the fix" width={520}>
              <div style={{ fontFamily: FONTS.mono, fontSize: 21, lineHeight: 1.9, color: COLORS.text }}>
                <div>orders by <b style={{ color: COLORS.green }}>created_at DESC</b></div>
                <div>not by cosine distance</div>
                <div style={{ color: COLORS.dim }}>the recency primitive RAG lacked</div>
              </div>
            </Panel>
          </div>
          <div style={{ opacity: fade, marginTop: 26 }}>
            <Callout label="eval probe · recency-probe.ts" accent="lime" width={780}>
              A black-box assertion that <b style={{ color: COLORS.text }}>fails the build</b> when a stale doc out-ranks a fresh one.
            </Callout>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={RECENCY} accent="green" />
    </AbsoluteFill>
  );
};

/* 07 — CITATION: click [Source N] → panel */
export const Scene07Citation: React.FC = () => {
  const frame = useCurrentFrame();
  const open = interpolate(frame, [40, 70], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.citation.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the trust trick · borrowed" color={COLORS.lime} />
          <div style={{ display: "flex", gap: 28, alignItems: "center" }}>
            <Panel accent="cyan" title="answer" width={520}>
              <div style={{ fontFamily: FONTS.body, fontSize: 24, lineHeight: 1.6, color: COLORS.text }}>
                The system uses RLS for tenant isolation{" "}
                <span style={{ color: COLORS.cyan, fontFamily: FONTS.mono, border: `1px solid ${COLORS.cyan}`, borderRadius: 5, padding: "1px 7px" }}>
                  [Source 1]
                </span>
                .
              </div>
            </Panel>
            <div style={{ fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim, opacity: open }}>→</div>
            <div style={{ opacity: open, transform: `translateX(${(1 - open) * 24}px)` }}>
              <Panel accent="lime" title="citation panel · the exact chunk" width={520}>
                <div style={{ fontFamily: FONTS.mono, fontSize: 18, lineHeight: 1.7, color: COLORS.dim }}>
                  <div style={{ color: COLORS.lime }}>document_chunks · id 7c3…</div>
                  <div>“…enforced via RLS policies on tenant_id…”</div>
                  <div>created_at · source · jump-to-doc</div>
                </div>
              </Panel>
            </div>
          </div>
          <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 28 }}>
            CitationPreviewPanel.tsx — no “trust me,” show the chunk.
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={CITATION} accent="lime" />
    </AbsoluteFill>
  );
};

/* 08 — PAYOFF: ~75% → peak 90% */
export const Scene08Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [120, 150], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const c2 = interpolate(frame, [240, 270], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="code-query retrieval accuracy" color={COLORS.lime} />
        <div style={{ display: "flex", alignItems: "baseline", gap: 18 }}>
          <span style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 60, color: COLORS.dim }}>~75%</span>
          <span style={{ fontFamily: FONTS.display, fontSize: 56, color: COLORS.dim }}>→</span>
          <MetricCounter value={90} from={75} startFrame={10} durationFrames={60} accent="lime" suffix="%" fontSize={150} />
        </div>
        <div style={{ fontFamily: FONTS.mono, fontSize: 22, color: COLORS.dim, marginTop: 10 }}>
          peak · on standard Postgres, no 1M-token context
        </div>
        <div style={{ display: "flex", gap: 30, marginTop: 36 }}>
          <div style={{ opacity: c1 }}>
            <Callout label="decision-tree prompt" accent="cyan" width={380}>
              killed the false <b style={{ color: COLORS.text }}>“not available”</b>
            </Callout>
          </div>
          <div style={{ opacity: c2 }}>
            <Callout label="recency + citations" accent="lime" width={380}>
              closed the <b style={{ color: COLORS.text }}>trust gap</b> vs NotebookLM
            </Callout>
          </div>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="lime" />
    </AbsoluteFill>
  );
};

/* 09 — OUTRO */
export const Scene09Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={64}>
          NotebookLM wins on one long doc.
          <br />
          A multi-source code KB needs{" "}
          <span style={{ color: COLORS.cyan }}>fixes you can measure.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · grounded retrieval
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
